import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';

import { TextFieldWrapper } from 'components/Input/TextFieldWrapper';
import { InputPasswordWrapper } from 'components/Input/InputPassword/InputPasswordWrapper';
import { CheckboxWrapper } from 'components/Input/CheckboxWrapper';
import { REGISTRATION_FORM_LABELS, REGISTRATION_FORM_NAMES } from 'shared/constants/form-constants';
import { urls } from 'shared/constants/routing.constants';
import CustomLink from 'components/CustomLink/CustomLink';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { RESPONSE_CODES } from 'shared/constants/validation-constants';
import { EModalsNames } from 'shared/types/modal.interface';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';

import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { RegistrationRequest, TError } from 'shared/types/api.interface';
import { useRegistrationMutation } from 'redux/api/auth.api';
import { useLoading } from 'shared/hooks/useLoading';
import { TRegistrationFormType, RegistrationFormValidation } from './validationSchema';

const fieldsArr = [
  REGISTRATION_FORM_NAMES.email,
  REGISTRATION_FORM_NAMES.first_name,
  REGISTRATION_FORM_NAMES.last_name,
  REGISTRATION_FORM_NAMES.password,
  REGISTRATION_FORM_NAMES.privacy_policy_agreed,
];

export const RegistrationForm = () => {
  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<TRegistrationFormType>({
    defaultValues: {
      [REGISTRATION_FORM_NAMES.email]: '',
      [REGISTRATION_FORM_NAMES.first_name]: '',
      [REGISTRATION_FORM_NAMES.last_name]: '',
      [REGISTRATION_FORM_NAMES.password]: '',
      [REGISTRATION_FORM_NAMES.privacy_policy_agreed]: false,
    },
    resolver: zodResolver(RegistrationFormValidation),
  });
  const dispatch = useAppDispatch();
  const [registration, { isLoading }] = useRegistrationMutation();
  useLoading(isLoading);

  const catchSubmitError = (errorsArr: TError[]) => {
    fieldsArr.forEach((field) => {
      setError(field, {
        type: 'custom',
        message:
          errorsArr.find((item: TError) => {
            if (field === REGISTRATION_FORM_NAMES.email) {
              return item.attr === field || item.code === RESPONSE_CODES.unique;
            }
            return item.attr === field;
          })?.detail || '',
      });
    });
  };

  const onSubmit: SubmitHandler<TRegistrationFormType> = async (data: RegistrationRequest) => {
    try {
      await registration(data).unwrap();
      // оставила тут диспатч, чтобы только из-за не него не создавать onQueryStarted внутри api,
      // тк по идее никаких других ошибок мы показывать тут и не должны
      dispatch(
        openCurrentModal({
          popupName: EModalsNames.ConfirmEmailModal,
          data: { currentEmail: data.email },
        }),
      );
    } catch (err) {
      // оставила тут отлавливание, тк почему-то, несмотря на типизацию error,
      // внутри try catch на ts не ругается на наличие data, а в useEffect на error из хука - ругается
      catchSubmitError(err?.data?.errors);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column' }}>
      <Stack
        alignItems="stretch"
        width="100%"
        sx={{ rowGap: { lg: '37px', md: '40px', sm: '40px', xs: '40px' } }}
      >
        <TextFieldWrapper
          name={REGISTRATION_FORM_NAMES.email}
          control={control}
          label={REGISTRATION_FORM_LABELS.email}
          errorMessage={errors.email ? (errors.email?.message as string) : ''}
        />
        <Grid container spacing="20px" sx={{ rowGap: { md: '17px', sm: '20px', xs: '20px' } }}>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <TextFieldWrapper
              name={REGISTRATION_FORM_NAMES.first_name}
              control={control}
              label={REGISTRATION_FORM_LABELS.first_name}
              errorMessage={errors.first_name ? (errors.first_name?.message as string) : ''}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <TextFieldWrapper
              name={REGISTRATION_FORM_NAMES.last_name}
              control={control}
              label={REGISTRATION_FORM_LABELS.last_name}
              errorMessage={errors.last_name ? (errors.last_name?.message as string) : ''}
            />
          </Grid>
        </Grid>
        <InputPasswordWrapper
          name={REGISTRATION_FORM_NAMES.password}
          control={control}
          label={REGISTRATION_FORM_LABELS.password}
          errorMessage={errors.password ? (errors.password?.message as string) : ''}
        />
        <InputPasswordWrapper
          name={REGISTRATION_FORM_NAMES.confirm_password}
          control={control}
          label={REGISTRATION_FORM_LABELS.confirm_password}
          errorMessage={errors.confirm_password ? (errors.confirm_password?.message as string) : ''}
        />
        <CheckboxWrapper
          name={REGISTRATION_FORM_NAMES.privacy_policy_agreed}
          control={control}
          label={
            <span>
              I agree to the&nbsp;
              <CustomLink
                href={urls.commonPages.privacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'underline',
                  fontSize: 'inherit',
                  fontWeight: 'normal',
                }}
              >
                Privacy Policy
              </CustomLink>
              *
            </span>
          }
          errorMessage={
            errors.privacy_policy_agreed ? (errors.privacy_policy_agreed?.message as string) : ''
          }
        />
        {/* <Stack sx={{ mt: '-25px', alignSelf: 'center' }}>
          <ReCAPTCHA onChange={handleCaptchaChange} sitekey={process.env.REACT_APP_SITE_KEY} />
        </Stack> */}
      </Stack>
      <Button
        sx={{ mt: { lg: '56px', md: '24px', sm: '24px', xs: '24px' }, alignSelf: 'center' }}
        variant="fixedSize"
        type="submit"
      >
        {FORMS_BUTTON.registrationSubmitButton}
      </Button>
    </form>
  );
};
